import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const AdminSettings = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  // Settings States
  const [settings, setSettings] = useState({
    siteName: 'Trianf Tutoring',
    maintenanceMode: false,
    autoApproveTutors: false,
    maxPendingDays: 14,
    supportEmail: 'support@trianf.com'
  });
  
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || 'https://trianf.com';

  // Fetch initial system configurations and metrics
  useEffect(() => {
    const fetchSystemData = async () => {
      try {
        // 1. Fetch settings data
        const settingsRes = await fetch(`${API_URL}/api/admin/settings`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setSettings(settingsData);
        }

        // 2. Fetch pending count for the badge notification
        const tutorsRes = await fetch(`${API_URL}/api/admin/pending-tutors`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (tutorsRes.ok) {
          const tutorsData = await tutorsRes.json();
          setPendingCount(tutorsData.length);
        }
      } catch (error) {
        console.error("Error loading administration data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemData();
  }, [API_URL]);

  // Security Gate validation
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="p-20 text-center">
        <h1 className="text-2xl pt-24 font-bold text-red-600">Access Denied</h1>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  const handleLogout = () => {
    if (logout) {
      logout();
      navigate('/login');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setMessage('System settings updated successfully!');
        setTimeout(() => setMessage(''), 4000);
      } else {
        throw new Error('Failed response configuration updating.');
      }
    } catch (err) {
      setMessage('Failed to commit configuration values to database.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <div className="pt-24 text-center text-blue-600 font-bold">Loading System Configurations...</div>;
  }

  return (
    <div className="flex pt-24 bg-gray-50 min-h-screen">
      {/* ================= SIDEBAR COMPONENT ================= */}
      <div className="w-64 bg-[#fbc901] text-white p-4 flex flex-col gap-4 shrink-0">
        <h2 className="text-xl font-bold border-b bg-blue-600 p-2 rounded pb-2">Admin Panel</h2>
        
        <Link 
          to="/tutoring/admin-dashboard" 
          className="bg-blue-600 hover:bg-blue-700 p-2 rounded font-medium transition duration-200"
        >
          Dashboard Home
        </Link>

        {currentUser?.role === 'admin' && (
          <Link 
            to="/support" 
            className='inline-flex items-center justify-between bg-blue-600 text-white px-4 py-2 rounded font-bold hover:bg-blue-700 transition duration-200'
          >
            <span>Tutor Approvals</span>
            {pendingCount > 0 && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] text-white animate-pulse">
                {pendingCount}
              </span>
            )}
          </Link> 
        )}
        
        <Link 
          to="/admin/settings" 
          className="bg-blue-700 p-2 rounded text-white font-bold transition duration-200 border-2 border-white/20"
        >
          System Settings
        </Link>
      </div>  

      {/* ================= MAIN CONTENT MODULE ================= */}
      <div className="flex-1 p-8 overflow-y-auto">
        {/* Global Dashboard Identity Bar */}
        <div className="mb-8 flex justify-between items-start bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Admin Portal: {currentUser.email}</h1>
            <span className="mt-2 inline-block bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider">
                System Administrator
            </span>
          </div>
          <button 
            onClick={handleLogout}
            className="bg-gray-800 text-white px-5 py-2 rounded-lg font-bold text-sm hover:bg-red-600 transition-all shadow-sm"
          >
            Logout Securely
          </button>
        </div>

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">System Parameters & Controls</h2>
          <p className="text-sm text-gray-500">Configure global platform configurations, security gates, and notification emails.</p>
        </div>
        
        {message && (
          <div className={`mb-6 p-4 border-l-4 rounded shadow-sm ${message.includes('successfully') ? 'bg-green-50 border-green-500 text-green-700' : 'bg-red-50 border-red-500 text-red-700'}`}>
            <p className="font-bold">{message}</p>
          </div>
        )}

        {/* Configuration Parameter Card */}
        <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-6 max-w-4xl">
          <form onSubmit={handleSaveSettings} className="space-y-6">
            
            {/* Section 1: Core Definitions */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Platform Brand Environment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Platform Application Name</label>
                  <input 
                    type="text" 
                    name="siteName"
                    value={settings.siteName}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Global Support Communication Target</label>
                  <input 
                    type="email" 
                    name="supportEmail"
                    value={settings.supportEmail}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Tutor Management Controls */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Tutor Onboarding Logic</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      name="autoApproveTutors"
                      checked={settings.autoApproveTutors}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label className="font-bold text-gray-700">Bypass Manual Review Pipeline</label>
                    <p className="text-gray-500 text-xs">Automatically verify profiles upon submission. If disabled, entries land on your table waiting approval.</p>
                  </div>
                </div>

                <div className="w-48">
                  <label className="block text-sm font-bold text-gray-700 mb-1">Stale Warning Threshold (Days)</label>
                  <input 
                    type="number" 
                    name="maxPendingDays"
                    value={settings.maxPendingDays}
                    onChange={handleInputChange}
                    min="1"
                    max="90"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Safety Variables */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Security & System Maintenance</h3>
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    name="maintenanceMode"
                    checked={settings.maintenanceMode}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label className="font-bold text-red-700 uppercase tracking-wider text-xs">Lock Application (Maintenance State)</label>
                  <p className="text-gray-500 text-xs">Locks routing targets down for students and tutors, displaying a standard upgrade placeholder page.</p>
                </div>
              </div>
            </div>

            {/* Action Group Block */}
            <div className="pt-4 border-t flex justify-end gap-2">
              <button
                type="button"
                onClick={() => navigate('/tutoring/admin-dashboard')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 shadow-sm transition disabled:opacity-50"
              >
                {isSaving ? 'Saving Configurations...' : 'Save Changes'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;